import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, X, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LogoutModal: React.FC<LogoutModalProps> = ({ isOpen, onClose }) => {
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('Signout failed (redirecting anyway):', err);
    }
    
    // Clear any local application state if necessary
    localStorage.removeItem('lastVenueId');
    
    // Navigate to login
    navigate('/login', { replace: true });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-md bg-brand-surface border border-white/10 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden"
        >
          {/* Accent Header */}
          <div className="h-2 w-full bg-gradient-to-r from-red-500 via-brand-primary to-orange-500" />
          
          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center text-red-500 border border-red-500/20">
                <LogOut size={28} />
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors text-gray-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-2 mb-8">
              <h3 className="text-2xl font-black text-white tracking-tight">Sign Out</h3>
              <p className="text-gray-400 font-medium leading-relaxed">
                Are you sure you want to end your session? Your progress in any unsaved configuration might be lost.
              </p>
            </div>

            <div className="p-4 bg-red-500/5 border border-red-500/10 rounded-2xl flex gap-3 text-red-400 mb-8 items-center">
              <AlertTriangle size={18} className="shrink-0" />
              <p className="text-xs font-bold uppercase tracking-wider">Destructive Action</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={onClose}
                disabled={isLoggingOut}
                className="py-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl font-bold transition-all border border-white/10 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="py-4 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-black shadow-xl shadow-red-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoggingOut ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign Out</>
                )}
              </button>
            </div>
          </div>

          {/* Footer Branding */}
          <div className="px-8 py-4 bg-white/5 border-t border-white/10 flex justify-center">
             <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                TableGreet Secure Hub
             </span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
